// src/App.js
import React, { useEffect, useState, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";

import Header from "./components/Header";
import Home from "./components/Home";
import MainLayout from "./components/MainLayout";
import OwnerDashboard from "./components/OwnerDashboard";
import TradeDetail from "./components/TradeDetail";
import TradeOfferDetail from "./components/TradeOfferDetail";
import TradeOfferPage from "./components/TradeOfferPage";

import ProtectedRoute from "./components/ProtectedRoute";
import TradingProtectedRoute from "./components/TradingProtectedRoute";
import PublicOnlyRoute from "./components/PublicOnlyRoute";
import { useAuth } from "./contexts/AuthContext";
import { auth, db } from "./firebase";

import UploadAd from "./components/UploadAd";
import BrowseTrades from "./pages/BrowseTrades";
import NotificationsPage from "./pages/NotificationsPage";
import NotificationEntryPoint from "./pages/NotificationEntryPoint";
import PreferencesPage from "./pages/PreferencesPage";
import InboxPage from "./pages/InboxPage";

import PostTradePage from "./components/PostTradePage";
import ChatView from "./components/ChatView";
import Register from "./components/Register";

import AdminRoute from "./pages/AdminRoute";
import TrustSafety from "./pages/TrustSafety";
import AdminToolsPage from "./pages/AdminTools";
import AdminDashboard from "./components/AdminDashboard";

import ProfilePage from "./pages/ProfilePage";
import UserSettings from "./components/UserSettings";
import LoginPage from "./components/LoginPage";
import VerifyCode from "./components/VerifyCode";
import PublicLayout from "./components/PublicLayout";

import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import HowTradesWork from "./pages/HowTradesWork";
import WhyNoDirectChat from "./pages/WhyNoDirectChat";
import FAQ from "./pages/FAQ";
import HelpCenter from "./pages/HelpCenter";
import Support from "./pages/Support";
import ReportItem from "./pages/ReportItem";
import DataDeletion from "./pages/data-deletion";

import CheckoutStub from "./pages/CheckoutStub";
import PayfastReturn from "./pages/PayfastReturn";
import PayfastCancel from "./pages/PayfastCancel";
import SponsoredCheckoutStub from "./pages/SponsoredCheckoutStub";

import PayRedirect from "./pages/PayRedirect";
import InspectionScanPage from "./pages/InspectionScanPage";
import InspectionHomePage from "./pages/InspectionHomePage";

import UserAccessManagement from "./pages/admin/UserAccessManagement";
import ProtectedAccessRoute from "./components/access/ProtectedAccessRoute";

import RecommendedTrades from "./components/RecommendedTrades";

import { Api } from "./lib/api"; // logout only
import "./App.css";

import { useLayoutEffect } from "react";
import { PopupProvider } from "./contexts/PopupContext";
import MyOffers from "./pages/MyOffers";

function LegacySingleOfferRedirect() {
  const [params] = useSearchParams();

  const tradeId = params.get("tradeId");
  const offerId = params.get("offerId");

  if (!tradeId) {
    return <Navigate to="/account/offers" replace />;
  }

  const query = new URLSearchParams();

  if (offerId) query.set("offerId", offerId);
  query.set("from", "legacy-offer-route");

  return (
    <Navigate
      to={`/trade/${encodeURIComponent(tradeId)}?${query.toString()}`}
      replace
    />
  );
}

function LegacyTradeOffersRedirect() {
  const { tradeId } = useParams();
  const [params] = useSearchParams();

  if (!tradeId) {
    return <Navigate to="/account/offers" replace />;
  }

  const offerId = params.get("offerId");

  const query = new URLSearchParams();

  if (offerId) query.set("offerId", offerId);
  query.set("from", "legacy-offers-detail-route");

  return (
    <Navigate
      to={`/trade/${encodeURIComponent(tradeId)}?${query.toString()}`}
      replace
    />
  );
}


const AppContent = () => {
  const { currentUser: ctxUser, loading } = useAuth();

  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Search term is app-level (Header -> Home/Browse behavior)
  const [searchTerm, setSearchTerm] = useState("");

  // Kept as a hook for Home to signal refresh/reset if needed
  // (value intentionally unused here)
  const [, setFetchedTrades] = useState([]);

  const navigate = useNavigate();

  const location = useLocation();

  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/verify";

  const handleLogoClick = () => {
    setSearchTerm("");
    navigate("/", {
      replace: true,
      state: { refresh: Date.now(), clearFilters: true },
    });
  };

 
useEffect(() => {
  if (loading) return;

  const minVisibleMs = 600;
  const startedAt = window.__NZ_BOOT_STARTED_AT__ || Date.now();
  const elapsed = Date.now() - startedAt;
  const wait = Math.max(0, minVisibleMs - elapsed);

  const t = setTimeout(() => {
    const loader = document.getElementById("app-boot-loader");
    if (!loader) return;

    loader.classList.add("is-hidden");

    window.setTimeout(() => {
      try {
        loader.remove();
      } catch {}
    }, 220);
  }, wait);

  return () => clearTimeout(t);
}, [loading]);

  function ScrollToTop() {
    const { pathname, search, hash } = useLocation();

    useLayoutEffect(() => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }

      if (hash) return;

      const scrollContainer =
        document.querySelector(".marketplace-content") ||
        document.scrollingElement ||
        document.documentElement ||
        document.body;

      try {
        scrollContainer.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      } catch {
        scrollContainer.scrollTop = 0;
        scrollContainer.scrollLeft = 0;
      }

      window.scrollTo(0, 0);
    }, [pathname, search, hash]);

    return null;
  }

  const checkFirstTimeLogin = useCallback(
    async (uid) => {
      if (!uid) {
        console.warn("🚫 checkFirstTimeLogin called with invalid UID.");
        return;
      }
      try {
        const userRef = db.collection("users").doc(uid);
        const userSnap = await userRef.get();

        const prefRef = db.collection("preferences").doc(uid);
        const prefSnap = await prefRef.get();

        if (!prefSnap.exists) {
          await prefRef.set(
            {
              location: {
                country: "South Africa",
                latitude: -26.2041,
                longitude: 28.0473,
              },
              preferredRadius: 100,
            },
            { merge: true }
          );
          console.log("✅ Seeded default preferences for new user.");
        }

        if (userSnap.exists && !userSnap.data().preferencesSet) {
          navigate("/preferences");
        }
      } catch (err) {
        if (err.code === "unavailable" || (err.message || "").includes("network")) {
          console.warn("🌐 Network issue while checking preferences. Will retry later.");
        } else {
          console.error("🔥 Error checking/seeding preferences:", err?.name, err?.message);
        }
      }
    },
    [navigate]
  );

  // Only run preference check when AuthContext user is available
  useEffect(() => {
    if (loading) return;
    if (ctxUser?.uid) {
      checkFirstTimeLogin(ctxUser.uid);
    }
  }, [ctxUser, loading, checkFirstTimeLogin]);

  useEffect(() => {
    const resetTimer = () => {
      localStorage.setItem("loginTime", Date.now().toString());
    };
    window.addEventListener("click", resetTimer);
    return () => window.removeEventListener("click", resetTimer);
  }, []);

  const LOGOUT_FENCE_KEY = "nz_logoutFenceUntil";

  const handleLogout = async () => {
    try {
      // Fence for ~3 seconds (prevents instant cookie upgrade loops)
      localStorage.setItem(LOGOUT_FENCE_KEY, String(Date.now() + 3000));

      localStorage.removeItem("rememberMe");

      // Clear cookie session (if available)
      if (Api?.logout) {
        await Api.logout().catch(() => {});
      }

      // Clear Firebase session
      await auth.signOut().catch(() => {});

      sessionStorage.removeItem("hasVisited");
      localStorage.removeItem("loginTime");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handlePostTrade = () => {
    const randomKey = Date.now();
    navigate("/market/post-trade", { state: { postKey: randomKey } });
  };



  return (
    <>

      {!isAuthPage && (
        <Header
          user={ctxUser}
          onLogoClick={handleLogoClick}
          onLoginClick={() => navigate("/login")}
          onLogoutClick={handleLogout}
          onPostTrade={handlePostTrade}
          onOpenProfile={() => setShowProfile(true)}
          onOpenSettings={() => setShowSettings(true)}
          onOpenPreferences={() => navigate("/preferences")}
          onSearch={(term) => setSearchTerm(term)}
        />
      )}

      <ScrollToTop />

      {showProfile && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-button" onClick={() => setShowProfile(false)}>
              ✖
            </button>
            <h2>👤 Profile</h2>
            <p>Profile details go here...</p>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-button" onClick={() => setShowSettings(false)}>
              ✖
            </button>
            <h2>⚙️ Settings</h2>
            <p>Settings content goes here...</p>
          </div>
        </div>
      )}

      <Routes>
        {/* =======================================================================
            PUBLIC + AUTHED (BOTH STATES) — wrapped in MainLayout
           ======================================================================= */}
        <Route element={<MainLayout />}>
          <Route
            path="/"
            element={
              <Home
                onLogoClick={handleLogoClick}
                onPostTrade={handlePostTrade}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setFetchedTrades={setFetchedTrades}
              />
            }
          />

          {/* PUBLIC browse + trade detail for guests */}
      
          <Route path="/market/browse" element={<BrowseTrades />} />
          <Route path="/trade/:id" element={<TradeDetail />} />

          <Route path="/payfast/return" element={<PayfastReturn />} />
          <Route path="/payfast/cancel" element={<PayfastCancel />} />
          <Route path="/pay/redirect" element={<PayRedirect />} />

          {/* Public trust perimeter */}
          <Route path="/notifications" element={<NotificationEntryPoint />} />
          <Route path="/unauthorized" element={<div>🚫 Unauthorized Access</div>} />
          <Route path="/trust-safety" element={<TrustSafety />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/help/how-trades-work" element={<HowTradesWork />} />
          <Route path="/help/why-no-direct-chat" element={<WhyNoDirectChat />} />
          <Route path="/data-deletion" element={<DataDeletion />} />

          {/* Convenience redirect */}
          <Route path="/market" element={<Navigate to="/market/browse" replace />} />

          {/* =======================================================================
              AUTHENTICATED AREA — still protected, same MainLayout shell
             ======================================================================= */}
          <Route element={<ProtectedRoute />}>
            <Route path="/preferences" element={<PreferencesPage />} />

            <Route element={<TradingProtectedRoute />}>
              <Route path="/trade/:id/offer" element={<TradeOfferPage />} />
              <Route path="/market/post-trade" element={<PostTradePage />} />
              <Route path="/account/offers" element={<MyOffers />} />
              <Route path="/checkout/:tradeId" element={<CheckoutStub />} />
              <Route path="/checkout/sponsored/:listingId" element={<SponsoredCheckoutStub />} />
              <Route path="/upload-ad" element={<UploadAd />} />
              <Route path="/OwnerDashboard" element={<OwnerDashboard />} />
            </Route>

            <Route path="/market/notifications" element={<NotificationsPage />} />

            {/* My Offers page legacy path */}
            <Route path="/market/offers" element={<Navigate to="/account/offers" replace />} />

            {/* Old offer-detail path now redirects to the normal trade page */}
            <Route path="/market/offers/:tradeId" element={<LegacyTradeOffersRedirect />} />

            {/* New cleaner seller offer route - keep this */}
            <Route path="/trade/:tradeId/offers" element={<TradeOfferDetail />} />

            <Route path="/support" element={<Support />} />
            <Route path="/report/:tradeId" element={<ReportItem />} />
            <Route path="/market/inbox" element={<InboxPage />} />

            {/* Old swap offer route now redirects to the normal trade page */}
            <Route path="/market/offer" element={<LegacySingleOfferRedirect />} />

            <Route path="/market/post-trade" element={<PostTradePage />} />
            <Route path="/inbox/:chatId" element={<ChatView />} />
            <Route path="/upload-ad" element={<UploadAd />} />
            <Route path="/checkout/:tradeId" element={<CheckoutStub />} />
            <Route path="/OwnerDashboard" element={<OwnerDashboard />} />
            
            <Route path="/account/offers" element={<MyOffers />} />
            <Route path="/settings" element={<UserSettings />} />
            <Route path="/checkout/sponsored/:listingId" element={<SponsoredCheckoutStub />} />
            <Route
              path="/market/recommended"
              element={
                <RecommendedTrades
                  searchTerm={searchTerm}
                />
              }
            />

            {/* Inspection route: only active users with inspectionReview */}
           <Route
              path="/inspection/scan"
              element={
                <ProtectedAccessRoute requirePermission="inspectionReview" fallbackPath="/unauthorized">
                  <InspectionScanPage />
                </ProtectedAccessRoute>
              }
            />

            <Route
              path="/inspection"
              element={
                <ProtectedAccessRoute requirePermission="inspectionReview" fallbackPath="/unauthorized">
                  <InspectionHomePage />
                </ProtectedAccessRoute>
              }
            />

            {/* Admin tools route: protect with admin role or a suitable permission later */}
            <Route
              path="/admin/tools"
              element={
                <ProtectedAccessRoute requireRole="admin" fallbackPath="/unauthorized">
                  <AdminToolsPage />
                </ProtectedAccessRoute>
              }
            />

            {/* Existing admin dashboard kept intact */}
            <Route
              path="/AdminDashboard"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />

            {/* User access management: only active users with roleManagement */}
            <Route
              path="/admin/user-access"
              element={
                <ProtectedAccessRoute requirePermission="roleManagement" fallbackPath="/unauthorized">
                  <UserAccessManagement />
                </ProtectedAccessRoute>
              }
            />

            {/* Catch-all for authed-only /market/* routes */}
            
          </Route>
        </Route>

        {/* =======================================================================
            PROFILE PAGE — protected but outside MainLayout to prevent layout squeeze
          ======================================================================= */}
        <Route element={<ProtectedRoute />}>
          <Route path="/profile" element={<ProfilePage />} />
        </Route>

        {/* =======================================================================
            AUTH PAGES ONLY — full-width PublicLayout (no sidebar)
           ======================================================================= */}
        <Route element={<PublicOnlyRoute redirectTo="/" />}>
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify" element={<VerifyCode />} />
          </Route>
        </Route>

        {/* Global catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

const App = () => (
  <Router>
    <PopupProvider>
      <AppContent />
    </PopupProvider>
  </Router>
);

export default App;